/**
 * Location Filter Composable
 * Provides reactive location IDs from parent window URL for filtering widgets
 * Listens for LocationPickerChanged messages from parent window
 */

import { ref } from 'vue'
import { getLocationIdsFromParentURL } from '../utils/locationFilter.js'

/**
 * Composable that provides location IDs from parent window URL
 * Updates reactively when:
 * 1. LocationPickerChanged message is received from parent window
 * 2. Parent URL changes (via polling as fallback, stops after first LocationPickerChanged message)
 * @returns {import('vue').Ref<Array<string>>} Reactive ref containing location IDs
 */

// Shared state across all instances to track if we've received LocationPickerChanged
let hasReceivedLocationPickerMessage = false
let globalMessageListener = null
let globalPollInterval = null
let locationIdsRef = null

export function useLocationFilter() {
  // Use shared ref if it exists, otherwise create new one
  if (!locationIdsRef) {
    locationIdsRef = ref([])
    
    const updateLocationIds = () => {
      // Only poll if we haven't received a LocationPickerChanged message yet
      if (!hasReceivedLocationPickerMessage) {
        const ids = getLocationIdsFromParentURL()
        // Only update if changed to avoid unnecessary reactivity triggers
        if (JSON.stringify(ids) !== JSON.stringify(locationIdsRef.value)) {
          locationIdsRef.value = ids
        }
      }
    }
    
    // Initial update from URL
    updateLocationIds()
    
    // Shared message listener for LocationPickerChanged events from parent window
    globalMessageListener = (event) => {
      // Listen for LocationPickerChanged message from parent
      if (event.data && typeof event.data === 'object') {
        // Handle LocationPickerChanged command
        if (event.data.command === 'LocationPickerChanged' || event.data.type === 'LocationPickerChanged') {
          hasReceivedLocationPickerMessage = true
          
          // Stop polling once we've received a LocationPickerChanged message
          if (globalPollInterval) {
            clearInterval(globalPollInterval)
            globalPollInterval = null
            if (process.env.NODE_ENV === 'development') {
            }
          }
          
          const payload = event.data.payload || event.data.locations || event.data.location_ids || event.data
          
          let newIds = []
          
          // Handle different payload formats
          if (Array.isArray(payload)) {
            // Direct array of location IDs
            newIds = payload.map(id => String(id).trim()).filter(id => id.length > 0)
          } else if (typeof payload === 'string') {
            // Comma-separated string
            newIds = payload.split(',').map(id => id.trim()).filter(id => id.length > 0)
          } else if (payload && typeof payload === 'object') {
            // Object with locations or location_ids property
            const locations = payload.locations || payload.location_ids || payload.locationIds
            if (Array.isArray(locations)) {
              newIds = locations.map(id => String(id).trim()).filter(id => id.length > 0)
            } else if (typeof locations === 'string') {
              newIds = locations.split(',').map(id => id.trim()).filter(id => id.length > 0)
            }
          }
          
          // Update location IDs if changed
          if (JSON.stringify(newIds) !== JSON.stringify(locationIdsRef.value)) {
            if (process.env.NODE_ENV === 'development') {
            }
            locationIdsRef.value = newIds
          }
        }
        // Also handle generic locations update (for backward compatibility)
        else if (event.data.locations && !hasReceivedLocationPickerMessage) {
          hasReceivedLocationPickerMessage = true
          
          // Stop polling once we've received a location update
          if (globalPollInterval) {
            clearInterval(globalPollInterval)
            globalPollInterval = null
          }
          
          const newIds = Array.isArray(event.data.locations) 
            ? event.data.locations.map(id => String(id).trim()).filter(id => id.length > 0)
            : event.data.locations.split(',').map(id => id.trim()).filter(id => id.length > 0)
          
          if (JSON.stringify(newIds) !== JSON.stringify(locationIdsRef.value)) {
            if (process.env.NODE_ENV === 'development') {
            }
            locationIdsRef.value = newIds
          }
        }
      }
    }
    
    // Set up global listener and polling only once
    if (typeof window !== 'undefined') {
      window.addEventListener('message', globalMessageListener)
      
      // Poll as fallback (every 10 seconds) - only until first LocationPickerChanged message
      globalPollInterval = setInterval(updateLocationIds, 10000)
      
      if (process.env.NODE_ENV === 'development') {
      }
    }
  }
  
  // Cleanup is handled globally, but we can track instance count if needed
  // For now, we'll keep the global listener active for the lifetime of the app
  
  return locationIdsRef
}
